// Mitarbeiter anlegen und verwalten — läuft über /api/konten?action=users, weil das
// Anlegen eines Kontos den service_role-Key braucht (der nie in den Browser darf).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type Rolle = "admin" | "buero" | "dispatch" | "fahrer";

export const ROLLEN: { wert: Rolle; label: string; hint: string }[] = [
  { wert: "fahrer", label: "Fahrer", hint: "Packliste, Tour, Kasse" },
  { wert: "buero", label: "Büro", hint: "Kunden, Rechnungen, Neuigkeiten" },
  { wert: "dispatch", label: "Disposition", hint: "Touren planen" },
  { wert: "admin", label: "Administrator", hint: "alles, inklusive Verwaltung" },
];

async function ruf(body: Record<string, unknown>) {
  const { data: session } = await supabase.auth.getSession();
  const res = await fetch("/api/konten?action=users", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.session?.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error ?? "Fehlgeschlagen");
  return json;
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["team"] });
  qc.invalidateQueries({ queryKey: ["zeit-alle"] });
}

export function useMitarbeiterAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: {
      email: string;
      password: string;
      full_name: string;
      role: Rolle;
      /** Leer lassen → der Server vergibt die nächste freie Nummer. */
      personalnummer?: string;
      urlaubstage_jahr?: number;
      /** false = Zugang ohne Anstellung (Entwickler): keine Personalnummer. */
      im_lohn?: boolean;
    }) => ruf({ action: "create", ...m }),
    onSuccess: () => invalidate(qc),
  });
}

/**
 * Die nächste freie Personalnummer — vom Server, nicht aus dem Browser geraten.
 *
 * Der Client sieht nicht zuverlässig alle vergebenen Nummern (RLS, Cache). Eine
 * doppelte Personalnummer würde in der Lohnbuchhaltung zwei Leute zusammenfallen
 * lassen, und das merkt man erst, wenn das Geld auf dem falschen Konto ist.
 */
export function useNaechstePersonalnummer(aktiv: boolean) {
  return useQuery({
    queryKey: ["naechste-personalnummer"],
    enabled: aktiv,
    staleTime: 0, // beim nächsten Anlegen ist sie vielleicht schon vergeben
    queryFn: async () => {
      const r = (await ruf({ action: "naechste_personalnummer" })) as {
        personalnummer: string;
      };
      return r.personalnummer;
    },
  });
}

export function useMitarbeiterAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: {
      id: string;
      full_name?: string;
      role?: Rolle;
      password?: string;
      /** Echte Rechte (z.B. produkte.anlegen) — die Datenbank setzt sie durch. */
      permissions?: string[];
      /** NULL/leer = alle Standard-Module. Sonst: nur diese im Menü. */
      allowed_modules?: string[] | null;
      /** Für den Lohn-Export — muss zur Personalnummer in DATEV passen. */
      personalnummer?: string | null;
      urlaubstage_jahr?: number;
      /** false = Zugang ohne Anstellung (Entwickler): keine Personalnummer. */
      im_lohn?: boolean;
    }) => ruf({ action: "update", ...m }),
    onSuccess: () => invalidate(qc),
  });
}

export function useMitarbeiterLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ruf({ action: "delete", id }),
    onSuccess: () => invalidate(qc),
  });
}

/** Vorschlag für ein Startpasswort — merkbar, aber nicht zu erraten. */
export function passwortVorschlag() {
  const woerter = ["Hof", "Ei", "Feld", "Korb", "Hahn", "Acker", "Scheune", "Halm"];
  const w = woerter[Math.floor(Math.random() * woerter.length)];
  const n = Math.floor(1000 + Math.random() * 9000);
  return `${w}${n}!`;
}
